import { styled } from '@mui/material'
import { isNotNullOrUndefined } from 'ComponentLibrary/src/common/type-predicates'
import { useEffect, useMemo, useState } from 'react'
import { useStablePresignedUrls } from './useStablePresignedUrls'

type BaseImageProps = {
    width?: number | string
    height?: number | string
    src: React.ImgHTMLAttributes<HTMLImageElement>['src']
    alt: string
    loading?: 'eager' | 'lazy'
    className?: string
    fallbackImageSrc?: string[]
    objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down'
}

/**
 * Base image component
 * @param objectFit {@link https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit}
 * @param fallbackImageSrc If the image fails to load, this image will be used instead
 * @param loading {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-loading}
 */
function BaseImage({
    className,
    width = '100%',
    height = '100%',
    src,
    alt,
    loading = 'lazy',
    objectFit = 'cover',
    fallbackImageSrc,
}: BaseImageProps) {
    const fallbackSources = useMemo(() => {
        if (!fallbackImageSrc) {
            return []
        }

        return fallbackImageSrc.filter(isNotNullOrUndefined)
    }, [fallbackImageSrc])
    const rawImageSources = useMemo(() => [src, ...fallbackSources].filter(isNotNullOrUndefined), [src, fallbackSources])
    const imageSources = useStablePresignedUrls(rawImageSources)

    const [imageIndex, setImageIndex] = useState(0)

    useEffect(() => {
        setImageIndex(0)
    }, [imageSources])

    const onErrorHandler = () => {
        const nextImageSrc = imageSources[imageIndex + 1]

        if (!nextImageSrc) {
            return
        }

        setImageIndex((currentIndex) => currentIndex + 1)
    }

    return (
        <img
            className={className}
            src={imageSources[imageIndex]}
            alt={alt}
            loading={loading}
            width={width}
            height={height}
            onError={onErrorHandler}
            style={{ objectFit }}
        />
    )
}

export const Image = styled(BaseImage)`
    width: ${(props) => `${props.width}px`};
    height: ${(props) => `${props.height}px`};
`

export const RoundImage = styled(BaseImage)`
    border-radius: 4px;
    width: ${(props) => `${props.width}px`};
    height: ${(props) => `${props.height}px`};
`

export const CircularImage = styled(BaseImage)`
    border-radius: 50%;
    width: ${(props) => `${props.width}px`};
    height: ${(props) => `${props.height}px`};
    // Display flex to center alt text in middle of image
    display: flex;
    color: white;
    justify-content: center;
    align-items: center;
`
